import { useCallback, useEffect, useRef } from 'react';

/**
 * Helper hook that implements managed timeouts.
 *
 * Managed timeouts are like regular `setTimeout` calls, but that
 * automatically cancel themselves when the component unmounts.
 */
export function useManagedTimeouts() {
  const ref = useRef(new Set<number | NodeJS.Timeout>());
  const unmountRef = useRef(false);
  unmountRef.current = false;

  const setManagedTimeout = useCallback(
    (callback: () => unknown, ms: number) => {
      if (unmountRef.current) return -1 as never;

      const timeout = setTimeout(() => {
        ref.current.delete(timeout);
        callback();
      }, ms);
      ref.current.add(timeout);
      return timeout;
    },
    []
  );

  const clearManagedTimeout = useCallback(
    (timeout: number | NodeJS.Timeout) => {
      clearTimeout(timeout);
      ref.current?.delete(timeout);
    },
    []
  );

  // clean up on unmount
  useEffect(() => {
    const timeouts = ref.current;
    return () => {
      unmountRef.current = true;

      for (const timeout of timeouts) clearTimeout(timeout);
      timeouts.clear();
    };
  }, []);

  return { setManagedTimeout, clearManagedTimeout };
}
